import * as Sentry from '@sentry/vue'
import { computed, ref } from 'vue'
import { api } from '@/utils/apiClient'
import { isMarketingHost } from '@/utils/platformHost'

// FeatureFlag mirrors internal/features/catalog.go. Keep in sync with
// the Go constants — the staff console UI lists every catalog row, but
// this union is what gives compile-time safety in TS callsites.
//
// To add a flag: add the Go constant in catalog.go, seed it via a
// migration, and add it to this union.
export type FeatureFlag =
  // Action-points capture surfaces (epic #2130): summary + ad-hoc
  // points with owner/due date, service-type templates, dashboard
  // list, activity-log proof, search. Default OFF; flipped in
  // lockstep with meetings.reminders.
  | 'meetings.action-points'
  // Reminder layer on top of action points: due-date notifications
  // with one-tap done, non-blocking calendar event, auto-close on
  // document send. Default OFF; requires meetings.action-points.
  | 'meetings.reminders'
  // During-meeting workspace on the live meeting view (#2130 follow-up):
  // editable action points + an "Under møtet" note taken while the
  // meeting runs. Default OFF. Owner/due affordances still gate on
  // meetings.action-points / meetings.reminders.
  | 'meetings.live-workspace'
  // Redesigned møtevisning (variant A): immersive top bar + stage + tabbed
  // rail (notat/punkter/chat/logg), desktop and mobile, with the meeting
  // hoisted into a shell-owned overlay so navigating away minimises it
  // instead of dropping the call. Default OFF; the old stacked live view
  // still serves every tenant outside the rollout.
  | 'meetings.live-redesign'
  // Customer-detail Historikk feed. Default OFF; the API route and pill are
  // enabled together per org by staff.
  | 'crm.activity-feed'
  // 6-digit code alongside the signup verification link (#3316 option C), so
  // an operator who reads mail on a different device can finish in the tab
  // they already have open.
  //
  // Listed here for catalog parity, but NOT readable through this composable:
  // /verify lives on the marketing apex, where GET /api/features answers with
  // an empty map because no tenant resolves, so flagRef would report false
  // forever. The waiting screen switches on `code_entry` from the POST
  // /api/signup response instead — the same server-side flag read that
  // decided whether a code was minted and emailed. See
  // internal/features/catalog_signup.go.
  | 'signup.verification-code'
  | 'notes.sharing'
  | 'notes.mentions-v2'
  | 'notes.share-expiry'
  // Private images in notes. Gates write affordances only: paste interception,
  // camera/file picker and editor-originated upload signing. Readonly rendering
  // stays ungated so disabling the rollout does not brick saved notes.
  | 'notes.images'
  | 'integrations.google-calendar'
  // Per-kind email preferences UI (issue #569). Gates the
  // Varslinger card under Konto. Backend endpoints stay on; only the
  // UI surface is gated so staff can roll out per org via the console.
  | 'notifications.per-kind-email-prefs'
  | 'notifications.quiet-hours-ui'
  | 'calendar.multi-picker'
  // External-calendar delta sync (migration 429): the worker job that
  // invalidates the cached Google/Outlook events and pushes
  // calendar.external.changed so the grid refetches. Listed for catalog
  // parity only — nothing in the SPA gates on it. The dashboard
  // subscribes to the topic unconditionally, and a topic that never
  // fires is silence rather than a broken surface.
  | 'calendar.event-sync'
  // Treatment clinical-data surfaces (PainScale, TiltakChips,
  // treatment_journal writes, Innstillinger tiltak editor). Default
  // OFF — these produce GDPR Art. 9 health data and Stemne has no
  // DPA signed with operators yet. Base treatment chrome (plan
  // checklist + warning, sist gang date, series badge, live timer)
  // stays unconditionally.
  | 'treatment.full-journal'
  // In-admin databehandleravtale accept surface (Innstillinger →
  // Behandling). When on, the operator can read and accept the DPA,
  // which stamps the org and unlocks treatment.full-journal via its
  // capability predicate. Default OFF until the agreement text is
  // lawyer-approved; staff flip per-org or globally.
  | 'treatment.dpa-accept'
  // Universal databehandleravtale (art. 28) acceptance gate — applies to
  // every operator, not only clinical ones. When on, the router bounces an
  // operator with a stale/absent acceptance to a blocking accept screen and
  // onboarding cannot complete until they accept. Reads the same org
  // acceptance record as treatment.dpa-accept. Default OFF until the text
  // is lawyer-approved.
  | 'operator.dpa-accept'
  // Operatøravtale accept gate (#3025 P1). Sibling of operator.dpa-accept,
  // not a superset: the two agreements carry independent version stamps
  // (organizations.terms_* vs dpa_*). It applies when enabling Stemne
  // payments, not to ordinary dashboard navigation. Default OFF.
  | 'operator.terms-accept'
  // Operator-facing path that flips a past confirmed/rescheduled
  // booking to status='no_show'. Gates the "Marker som ikke møtt"
  // button in DashboardBookingDetailView AND the dedicated PATCH
  // /api/admin/bookings/{id}/no-show route (server-side enforced).
  // Default OFF while the UX gets dogfooded; staff flip per-org from
  // the console.
  | 'bookings.no-show-marking'
  // Staff-medlemmer kort på /staff/security. Gates the management UI
  // for adding + disabling stemne_staff rows from the console. Default
  // OFF until the surface has been verified; the direct SQL insert
  // still works as the bootstrap escape hatch.
  | 'staff.member-management'
  // Headless machine surface at /api/staff/social/machine (Fase 0): an
  // external scheduled agent creates DRAFT social posts via a staff service
  // token. Distinct from staff.social-posts (operator UI) so the two toggle
  // independently. Backend/staff-only (public_to_client FALSE) — the SPA
  // never reads it; the literal is here only to keep the union in sync with
  // internal/features/catalog.go.
  | 'staff.social-machine-api'
  // Media capability on the social machine surface (migration 326): gates the
  // machine uploads/sign route so the scheduled agent can attach a pre-rendered
  // image/video to a draft. Server/staff-only; the SPA never reads it — here
  // only to keep the union in sync with internal/features/catalog.go.
  | 'staff.social-machine-media'
  // Staff outreach ("Meldinger") approve-surface (Fase 1a): a staff member
  // reviews the scheduled agent's 1:1 outreach drafts (godkjenn/underkjenn/
  // rediger). staff.outreach-machine-api is the headless machine surface where
  // the external agent drafts them. Both backend/staff-only (public_to_client
  // FALSE) — the SPA never reads them; the literals keep the union in sync with
  // internal/features/catalog.go.
  | 'staff.outreach'
  | 'staff.outreach-machine-api'
  // staff.outreach-leads gates the outreach prospect ("Leads") console + its
  // machine read/create surface. Backend/staff-only (public_to_client FALSE) —
  // the Leads sub-tab degrades open on a 404; the literal keeps the union in
  // sync with internal/features/catalog.go.
  | 'staff.outreach-leads'
  // staff.ad-plan gates the monthly ad-plan surfaces: the machine write where the
  // stemne-annonseplan agent POSTs the month's theme + campaign roster, and the
  // operator read behind the marketing page's "Plan" tab. Server/staff-only.
  | 'staff.ad-plan'
  // Operator-facing timezone surfaces (setup-availability picker,
  // Innstillinger → Selskap tidssone card, read-only Tidssone line
  // in Konto → Varslinger). Default OFF: Stemne is Norway-first and
  // every effective zone is Europe/Oslo today. Staff flip per-org for
  // operators who legitimately run from another zone.
  | 'settings.timezone'
  // Post-setup welcome modal shown on the operator's first /dashboard
  // land after onboarding_step flips to 'done'. Pure rollout flag,
  // default OFF — staff dogfood per-org before a global flip.
  | 'onboarding.welcome-modal'
  // Plan-upgraded modal shown when a Stripe webhook flips the org's
  // plan tier upward. Distinct from onboarding.welcome-modal — the
  // trigger is a billing event, not a setup completion. Default OFF.
  | 'billing.plan-upgraded-modal'
  // Bulk-import-customers wizard on the Kunder page (upload →
  // koble kolonner → se gjennom). Gates the in-header "Importer
  // flere" outline button + the backing POST
  // /api/admin/customers/bulk-import route. Default OFF.
  | 'customers.bulk-import'
  // Document-sharing surface (operator archive + share/manage flows,
  // conversations inbox, shared room, customer portal extension).
  // Single bit gates the whole feature across both operator and
  // customer perspectives. Default OFF until each slice ships and is
  // dogfooded per-org. See docs/document-sharing-plan.md.
  | 'documents.sharing'
  // Global document search + categories (DOK-SEARCH-C). Rollout flag,
  // public_to_client. Gates the per-document "gjør søkbar" opt-in, the
  // category picker/manager, and the global "Dokumenter"-search surface +
  // facet. Default OFF; separate from search.indexing (which governs the
  // indexer worker). See docs/user-stories-document-search.md.
  | 'documents.search'
  // Application-level encryption for shared files (DPIA R7). Rollout flag,
  // public_to_client. Seeded in 354_documents_encryption.up.sql. Gates the
  // encrypted-upload path, the per-file "Krypter filen" toggle + badge, and
  // the DPA-stale share acknowledgment. Requires an accepted, current
  // databehandleravtale to actually encrypt. Default OFF across the 4-PR
  // rollout; reads are never flag-gated so a flag flip can't brick files.
  | 'documents.encryption'
  // Public integration API (#447). Capability flag — entitlement-backed
  // (org_entitlements row, written by billing webhook on Vekst purchase
  // or by staff grant). Gates: the `/dashboard/integrations` route +
  // sidebar item; the admin /integrations/tokens endpoints; the entire
  // `/api/v1/*` surface (404 when off, so the API looks absent).
  | 'integrations.public-api'
  // Extended video duration (video.extended-duration). Capability flag —
  // entitlement-backed (org_entitlements row, written by the billing webhook
  // on purchase or by staff grant). When ON, the org's video meetings may run
  // up to 120 min instead of the default 60-min cap. Drives the billing
  // "aktivert" state + hides the buy CTA. Default OFF.
  | 'video.extended-duration'
  // Fully Stemne-owned meeting surface on the Whereby Core SDK. The raw
  // iframe remains the default-off fallback while browser/device QA runs.
  | 'video.native-room'
  // Per-room Whereby branding (video.whereby-branding, #571/#3591/#3592/#3596).
  // Server-side it gates every Room Theme API call — colours, logo, and the
  // two meeting backgrounds. Made readable by the client in migration 434 so
  // the operator's "Møterom" settings panel hides under the SAME switch the
  // provider calls run under: one flag, one state, no way for the UI to
  // promise branding the backend is not sending. Default OFF.
  | 'video.whereby-branding'
  // SMS-reminders capability (notifications.sms-reminders). Now resolves
  // to "has ever purchased SMS credits". Gates the SMS opt-in surface in
  // notification settings; the worker gates every SMS send on the credit
  // balance. Default OFF.
  | 'notifications.sms-reminders'
  // Prepaid SMS packages (notifications.sms-packages). Rollout flag. Gates
  // the "Kjøp SMS-pakke" purchase surface + balance UI. Default OFF.
  | 'notifications.sms-packages'
  // Operator pre-meeting reminder (notifications.meeting-reminder).
  // Rollout flag. Gates the ~5-min imminent banner on the dashboard
  // shell + (worker-side) the 30-min KindReminder notification emit.
  // Default OFF.
  | 'notifications.meeting-reminder'
  // 24h participant reminder emails (notifications.participant-reminders).
  // Rollout flag. Worker-side gate: one email per host, co-host and
  // email invitee on team/group meetings. No client surface yet —
  // public so the booking-detail reminder pills can reflect the lane
  // later. Default OFF.
  | 'notifications.participant-reminders'
  // "Om oss"-bånd on the public booking page (#859). Gates the renderer
  // on the public booking landing AND the matching editor in
  // BrandingTab → Innstillinger. Default OFF; staff flips per-org once
  // the operator has filled the content in.
  | 'branding.about-section'
  // Global lobby banner (#745). Drives the top-strip "venter i
  // lobbyen" signal in the dashboard shell, sourced from the
  // Whereby room.client.knocked webhook + lobby.knocked WS topic.
  // Default OFF until the primitive is dogfooded on QA.
  | 'lobby.banner'
  // Dashboard "pågår nå" banner (migration 220). Drives the GlobalBanner
  // top-strip that lists open sessions (started, not yet finished) so a
  // meeting/treatment left running after the operator navigated away is
  // visible instead of silently ticking. Polls GET
  // /api/admin/bookings/active. Default OFF until dogfooded.
  | 'meetings.active-banner'
  // Live Whereby participant/session signals. The shell owns the sockets so
  // route changes do not multiply subscriptions; default OFF until the
  // webhook snapshots have been dogfooded in QA.
  | 'meetings.presence'
  // Whereby recording.finished (migration 425): stores a reference to a
  // finished recording against its booking. OFF still receives and
  // audit-logs the webhook — only the retained reference is skipped.
  // Backend-only, public_to_client FALSE: there is no operator surface
  // yet, and enabling it needs a DPA amendment and a DPIA revision first.
  // Listed so the staff console's flag union stays exhaustive. Default OFF.
  | 'meetings.recording'
  // Delt rom — S5 of document-sharing. Gates the /rom customer view
  // that replaces the document-list section of /portal with a
  // unified docs + chat thread. Default OFF until operator + customer
  // QA clears; staff flips per-org once a real consultancy is ready.
  // Distinct from documents.sharing so a tenant can run docs without
  // rooms. See docs/document-sharing-s5-plan.md.
  | 'rooms.shared-room'
  // Møtechat (migration 204): in-meeting chat between operator and
  // customer inside the video call. Gates the Chat panel in the meeting
  // view + the customer-detail "MØTECHAT … vis logg" fold. Møtechat
  // belongs to the meeting, not "samtaler". Default OFF.
  | 'meetings.chat'
  // Per-meeting free-text invite intro (migration 283/284): a short greeting
  // the host writes once on the prep view, included in every invitee_invited
  // email above the agenda. Gates the intro textarea. Default OFF.
  | 'meetings.invite-intro'
  // PDF-eksport av oppsummeringen etter møtet: operatørens nedlasting og
  // (senere) PDF-en som følger med e-posten til kunden. Gates knappen.
  // Default OFF.
  | 'meetings.wrap-pdf'
  // Eksplisitt valg i etter-møte-oppsummeringen: ta med møtenotatene som
  // ren tekst i kunde-e-posten. Default OFF.
  | 'meetings.wrap-include-notes'
  // Self-serve demo auto-login for the sales-demo tenant (migration 285).
  // Server-side gate only (public_to_client = false); listed here to keep
  // the union in sync with internal/features/catalog.go. Default OFF.
  | 'demo.self-serve-login'
  // Multi-invitee meetings + per-booking custom address (migration 206).
  // Gates the invite panel (re-enabled from #1304) and the meeting-address
  // input. Each invitee becomes a calendar attendee, gets a Stemne email +
  // .ics, and can join the meeting chat via their own link. Default OFF.
  | 'booking.invitees'
  // Multi-team-member meetings (migration 207). Operator adds several
  // consultants as participants; the system blocks the add if any is already
  // booked at that slot. Operator-only. Default OFF.
  | 'booking.team-meetings'
  // Change the møtested on an existing in-person booking (migration 218).
  // Notifies the customer (new address + fresh link) and updates connected
  // calendars. Operator-only. Default OFF.
  | 'booking.location-edit'
  // Time-poll voting (migrations 226-229). Operator sets candidate times,
  // attendees mark each works/doesn't, system auto-locks the winner (or the
  // operator commits). Attendees may suggest extra times for approval. Gates
  // the poll UI + vote endpoints + emails. Default OFF.
  | 'booking.time-poll'
  // Attendee-proposed reschedule (migration 243). On a fixed-time confirmed
  // booking, an attendee proposes a new time → the operator accepts (reschedules)
  // or declines (Google/Outlook "propose new time" model). The meeting stays put
  // until the operator accepts. Default OFF.
  | 'booking.suggest-time'
  // Team RSVP (migration 230). Team participants get a confirm/decline request
  // when added to a meeting; operator sees who has accepted. Default OFF.
  | 'booking.team-rsvp'
  // Bytt rådgiver (migration 251). Operator hands a meeting to another
  // consultant; advisor only own, owner/admin any. Default OFF.
  | 'booking.reassign-consultant'
  // Pending invite teammate added as an email "guest" co-host (migration 259).
  // Routes the guest through the email-invitee path instead of co_host_ids
  // (a pending invitee has no admin_users row). All meeting types. Default OFF.
  | 'booking.invitee-guests'
  // Kill switch for gruppesamtaler (migration 370). Gates the
  // "Gruppestørrelse" field on the service editor here, and server-side the
  // seat card ("X av Y plasser fylt") the customer sees. Default ON —
  // the capability shipped ungated and is already live for every org, so
  // this exists to turn it OFF, not to reveal something new. Turning it off
  // does NOT lift the seat limit on services that already have one.
  | 'booking.group-capacity'
  | 'customers.search-modal'
  // Full-screen samtaleflate. When on, the samtaler pill on the
  // customer detail page navigates to /dashboard/customers/{id}/samtale
  // instead of filtering the inline timeline. Default OFF until the
  // surface is dogfooded.
  | 'messaging.thread-view'
  // Meeting-referenced messages: a customer who writes from a specific
  // meeting's manage page sends an ordinary samtaler message tagged with
  // that booking, so it reaches the operator inbox + notifications and the
  // operator view shows a "om: <tjeneste> · <dato>" reference chip. The
  // in-call video møtechat is unaffected. Default OFF.
  | 'messaging.meeting-references'
  // Global message search (MSG-SEARCH): the "meldinger" source in smart søk
  // that decrypts + fuzzy-matches every samtale body on the request path.
  // Gates the /api/admin/messages/search endpoint + the modal source.
  // Decrypt-per-search — no message plaintext is indexed. Default OFF.
  | 'messaging.search'
  // Global agenda search (AGENDA-SEARCH): the "agenda" source in smart søk that
  // fuzzy-matches the meeting agenda points ("Hva må vi få svar på?", kind='ask').
  // Gates /api/admin/agenda/search + the modal source. All plain columns.
  // Default OFF.
  | 'meetings.agenda-search'
  // Redesigned customer portal ("Portalens jobb nr. 1"): next-meeting
  // card on top with .ics download, conversation as a chronological
  // thread with the composer at the bottom, documents + return-upload
  // together, konto & personvern as quiet links. Frontend-only gate;
  // when off the portal renders the existing pill-filtered timeline.
  | 'portal.next-meeting-first'
  // "Se alle dine avtaler" shortcut on the invitee meeting page
  // (/meeting?token=). When on, the page links into /portal with the
  // invitee's email prefilled (one tap to get the magic link). Frontend-only
  // gate; the magic-link flow still does the actual auth. Default OFF.
  | 'portal.meeting-access'
  // Prefill the public booking form's "Om deg" step (name, email, phone)
  // for a customer with a live portal session. When on, the details step
  // calls GET /api/portal/profile and fills the empty fields. Backend
  // gates the endpoint too (404 when off). Default ON.
  | 'portal.prefill-booking'
  // Operator analytics dashboard (#442): /dashboard/innsikt + the
  // GET /api/admin/analytics aggregate (bookinger over tid mot
  // forrige periode, ikke møtt-rate, belastning per rådgiver,
  // tjenestemiks). Gates the nav entry + view; the backend route
  // 404s independently via features.Require. Default OFF.
  | 'analytics.dashboard'
  // Rapporter — fakturagrunnlag (#2743): /dashboard/rapporter + the
  // GET /api/admin/reports/invoice-basis aggregate (gjennomførte timer
  // per kunde og tjeneste for én måned, ikke-møtt som egne rader, CSV-
  // eksport). Gates the nav entry + view; the backend route 404s
  // independently via features.Require. Default OFF.
  | 'reports.invoice-basis'
  // Gjenta (serie) picker in the operator's book-on-behalf wizard
  // (#1194). Frequency + a COUNT-or-UNTIL terminator, translated into
  // the RRULE subset POST /api/admin/bookings parses. Frontend-only
  // gate — the endpoint keeps accepting `recurrence` regardless, so
  // turning the flag off hides the control without stranding an org
  // mid-series. Default OFF.
  | 'booking.recurrence-operator'
  // Reverse-direction calendar sync (#399). When on, the worker
  // reconciles operator-made changes in Google (event moved/deleted)
  // back onto the Stemne booking, and the calendar manage view swaps
  // its "synkroniseringen går én vei" disclosure for the two-way
  // explanation. Default OFF until the reconcile loop is dogfooded
  // against a real operator calendar; staff flip per-org.
  | 'calendar.reconcile'
  // Nedgraderingsflyt med setevalg (#855). When an over-cap operator
  // picks a smaller plan, BillingTab intercepts with the member-
  // selection + reassignment modal and the backend stages a
  // pending_seat_changes row applied at period end. Gates the modal
  // surface + pending banner AND the three /api/admin/billing/
  // *downgrade* routes (404 when off). Default OFF until dogfooded.
  | 'billing.downgrade-flow'
  // Customer-detail timeline notifications: a moss dot on a filter pill
  // (møter, filer, notater, samtaler) when something new has arrived
  // since the operator last looked at that kind for that customer, plus
  // a header bell that jumps to the pill with new activity. The dots +
  // bell render purely from the timeline `unread` map (absent when off),
  // so this literal exists for completeness / explicit gating. Default OFF.
  | 'customer.timeline-notifications'
  // Cross-session continuity + follow-up points (epic #1869, Phase 1). Gates
  // the per-item assignee control + chip on PlanChecklist and the standing
  // canvas-points group on PriorSessionHistory. Backend ships the continuity
  // bundle fields (prior_note, prior_open_items, prior_canvas_points) behind
  // the same flag. Default OFF; staff flip per-org.
  | 'booking.session-continuity'
  // Rådgiver relationship-continuity hints (#1920): the operator booking detail
  // shows a "returnerende kunde / møtte sist" badge and the customer manage page
  // shows "du møter [navn] igjen" when reassigned to the same advisor. Default
  // OFF; staff flip per-org.
  | 'booking.advisor-continuity'

  // First-class training kind (epic #1869, track b). When ON, the operator can
  // create / select a 'trening' service and bookings against it (mode 'trening')
  // render the training-framed prep + live surface instead of therapy framing.
  // public_to_client TRUE: the SPA reads it to expose the kind option and switch
  // framing. Default ON since migration 287; staff hold an org back per-org.
  | 'booking.trening'

  // Per-service acknowledgment gate (migration 298). When ON, an operator can
  // mark a service "krever bekreftelse"; the customer must read the description
  // and tick "jeg har lest og forstått" before the booking confirms.
  // public_to_client TRUE: the SPA reads it to show the operator toggle AND to
  // render the customer gate. Default OFF; staff flip per-org.
  | 'booking.type-ack'

  // Rebook out of a meeting that already happened (#3490). ON adds a «Book ny
  // samtale» CTA under the read-only "Dette møtet er gjennomført" line on
  // /booking?token=…, and points the portal's empty-state button at the
  // booking flow with the previous service preselected instead of the bare
  // site root. Applies to the completed state only — «ikke gjennomført» and
  // «uavklart» stay informational. public_to_client TRUE: the SPA decides
  // whether to render either affordance. Default OFF; staff flip per-org,
  // because "book another" fits an ongoing rådgiver relationship and not a
  // one-off intro samtale.
  | 'booking.held-rebook'

  // Group departures (stories/09-group-departures.md, #1195). Gates the
  // "Fast avgang" section in the service editor, the departures page, and the
  // public departure picker. Backend 404s the departure routes when off.
  // Default OFF (migration 450).
  | 'booking.departures'

  // Per-operator service offerings (migration 271/272). When ON, operators opt
  // out of services they don't offer under Konto → Mine tjenester, and
  // owners/admins manage the whole team from a "Hvem tilbyr hva" matrix on the
  // services page (plus per-operator/per-service checklists in the member and
  // service edit modals). The auto-assigner honours exclusion rows regardless;
  // this flag only gates the management surfaces. public_to_client TRUE.
  // Default ON; staff can hold a specific org back.
  | 'services.operator-offerings'

  // Public booking gate (help-centre audit 2026-07-02): when ON, an
  // inactive subscription (paused / trial_expired / canceled) closes the
  // public booking page (booking_available=false in /api/public/tenant +
  // POST /api/bookings 403s). The dashboard branches the pause/cancel
  // copy on this flag so it never promises hiding while the gate is off.
  // Default OFF; staff flip globally once verified.
  | 'billing.public-booking-gate'

  // Automated org deletion 30 days after a canceled subscription's
  // access ended. The dashboard branches the cancel-flow copy on this
  // flag so it never promises deletion while the job is off. Default OFF.
  | 'billing.auto-hard-delete'

  // Audit-log retention reaper: read-access audit rows older than the
  // stated period are deleted, each reaped chain getting a watermark so
  // the deletion does not read as tampering. Worker-only,
  // public_to_client FALSE — no operator surface branches on it. Listed
  // so the staff console's flag union stays exhaustive. Default OFF.
  | 'audit.log-retention'

  // Mid-onboarding stall nudge (#3528): one email to an operator who
  // started the setup wizard and stopped, sent 24 to 48 hours after
  // signup with a deep link back to the step they left off on, before
  // the 72h reaper deletes the org. Worker-only, public_to_client
  // FALSE — no operator surface branches on it. Listed so the staff
  // console's flag union stays exhaustive. Default OFF.
  | 'operator.stall-nudge'

  // Closed-booking-page reminder (#3547): one email to an operator who
  // finished the setup wizard but never accepted the databehandleravtale,
  // whose booking page has been answering every visitor with "Booking er
  // ikke tilgjengelig" ever since. Worker-only, public_to_client FALSE —
  // no operator surface branches on it. Listed so the staff console's flag
  // union stays exhaustive. Default OFF.
  | 'operator.dpa-nudge'

  // Quiet-hours morning summary: emails suppressed by quiet hours are
  // collected and sent as one summary when the window ends. VarslingerTab
  // branches the toggle description on this flag. Default OFF.
  | 'notifications.quiet-hours-digest'
  // Scripted synthetic activity injected into the sales-demo tenant
  // (migration 299) so its dashboard reacts while a prospect watches.
  // Server-side gate only (public_to_client = false); listed here to keep
  // the union in sync with internal/features/catalog.go. Default OFF.
  | 'demo.live-activity'
  // Unified tag search (#2115): a top-bar search box that matches a tag
  // (or an object's label) across meetings, customers, and documents,
  // grouped by type, each hit clickable to its detail page. Gates the
  // dashboard search trigger + modal AND the GET /api/admin/search/tags
  // route (404 when off, so the surface looks absent). Default OFF; staff
  // flip per-org once dogfooded.
  | 'search.global'
  // Operator-side payment configuration per service (#1191, migration 381):
  // the "Betaling" section in the service edit modal and the
  // GET/PUT /api/admin/services/{id}/payment-config routes (404 when off).
  // Config only — declaring a deposit moves no money. Default OFF.
  | 'payments.config'
  // Customer-facing checkout in the booking flow (Vipps + card). Seeded
  // alongside payments.config but UNUSED until the checkout slice — a
  // separate lever on purpose, so stopping charges mid-incident doesn't also
  // lock the operator out of correcting the terms that caused it. Default OFF.
  | 'payments.checkout'
  // The one-line notice in the service editor for operators WITHOUT the
  // «Betaling ved booking» add-on, where the payment section renders nothing
  // (migration 393). Gates the discovery route, not the feature: since
  // payments.config became a capability behind the entitlement, the paywall
  // and the shop window became the same gate, so an org that has not bought
  // sees no trace of pay-at-booking while it is pricing a service. Default OFF.
  | 'payments.addon-teaser'
  // The evidence-triggered line under the no-show KPI on Innsikt, for orgs
  // without the add-on (migration 395). Renders only when the operator's own
  // no-show count for the period crosses the threshold, so it is
  // self-limiting and carries no dismiss state. Default OFF.
  | 'payments.no-show-nudge'
  // Collecting money AFTER the service was delivered (migration 399): the
  // remainder a deposit left, or the whole price on a «betaling etterpå»
  // service. Gates the worker consumer that turns a held meeting into a
  // payment request and emails the customer the link. The customer's pay page
  // and the operator's «betalingsforespørsel sendt» surface read it from the
  // boot response once they land. Default OFF.
  | 'payments.post-service'
  // The notice in the service editor for a PRICED service Stemne carries no
  // money for — no checkout at booking, no request after delivery (migration
  // 426). Since RequiresAngrerettConsent narrowed to obligations Stemne holds,
  // such a service captures no § 12 express request, and the angrerettloven
  // duty sits entirely with the operator. This gates telling them. Default OFF.
  | 'payments.self-billed-angrerett'

type FlagMap = Partial<Record<FeatureFlag, boolean>>

const flags = ref<FlagMap>({})
const loaded = ref(false)
const loading = ref(false)

// In-flight handle for load(). Module-scoped so a SECOND caller joins the
// in-flight fetch instead of resolving immediately — `if (loading) return`
// looked like idempotency but silently turned `await load()` into a no-op for
// everyone who arrived while another caller's load was still in the air.
//
// That is exactly what broke the #1887 DPA gate: the router's cold-load guard
// awaits the gate status, then reads useFeatures().peek('operator.dpa-accept')
// — which was still false because its `await load()` had returned early. The
// gate failed open and the operator booted into a dashboard where every data
// call 403s dpa_required. Callers that genuinely want a re-fetch use refresh().
let loadPromise: Promise<void> | null = null

// hasOnboardingSlug peeks at the sessionStorage onboarding store the same
// way apiClient's getTenantSlug() and useTenant do. During the 5-step setup
// a fresh operator sits on the marketing apex (stemne.no / qa.stemne.no /
// bare localhost) with the claimed subdomain only in this store — the
// apiClient forwards it as X-Tenant-Slug, so /api/features resolves for the
// new org even before the redirect to {slug}.stemne.no. Local helper rather
// than importing apiClient's getTenantSlug to keep this lookup synchronous
// and free of a Vue-runtime dep at module load.
function hasOnboardingSlug(): boolean {
  try {
    const raw = sessionStorage.getItem('stemne_onboarding')
    if (!raw) return false
    return Boolean((JSON.parse(raw) as { subdomain?: string }).subdomain)
  } catch {
    return false
  }
}

// useFeatures exposes a reactive feature flag store backed by
// GET /api/features. Load is idempotent; refresh forces a re-fetch
// (call after a staff write inside the same SPA session).
//
// Calls before load(): flagRef returns false. Treat "off" as the
// safe default rather than awaiting the resolution at every read site.
export function useFeatures() {
  async function fetchFeatures(attempt = 0) {
    // Skip on marketing apex — /api/features needs a tenant to resolve
    // overrides, so it 404s on stemne.no / qa.stemne.no. The marketing
    // site has no feature-gated surfaces; let flagRef keep returning
    // false on an unloaded store, which is the same posture as a real
    // 404 ("degrade open"). EXCEPT mid-onboarding: a setup-stage operator
    // is on apex with the claimed subdomain in the onboarding store, and
    // the setup steps DO gate on flags (booking.trening drives the trening
    // toggle on the services step). Without this the flag never loads there
    // and a coach's services all fall back to samtale. Mirrors useTenant.
    if (isMarketingHost() && !hasOnboardingSlug()) return
    loading.value = true
    try {
      const res = await api.get<{ flags: FlagMap }>('/api/features')
      const next = res.flags ?? {}
      // GetPublicFlags answers 200 {"flags": {}} when the request resolved
      // no tenant (apex fallback) — a resolved tenant always gets every
      // public flag back, false values included. App.vue refreshes on every
      // tab refocus, so mid-session that answer means "couldn't tell", not
      // "everything is off": replacing a loaded store with it would flip
      // every v-feature surface to hidden until a hard reload.
      if (!loaded.value || Object.keys(next).length > 0) {
        flags.value = next
        loaded.value = true
        // "Which gates were on for this operator when it broke" — the
        // question every flag-gated bug report needs answered, and the
        // one that is hardest to reconstruct after the fact (staff can
        // flip a default or an override between the error and the
        // triage). Copied, not aliased, so a later refresh doesn't
        // retroactively rewrite what an already-sent event said. Safe
        // no-op when no Sentry client is active (DSN unset in local dev).
        Sentry.setContext('feature_flags', { ...next })
      }
    } catch (e) {
      // Nothing refetches after boot, so a single transient failure
      // here used to leave every flag-gated element off for the page's
      // whole lifetime. Retry twice with backoff before giving up.
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)))
        return fetchFeatures(attempt + 1)
      }
      // Degrade open: failing to load features should not block the
      // app. Every flagRef call returns false on an unloaded store,
      // which matches the server-side "off by default" stance.
      console.error('Failed to load features:', e)
    } finally {
      loading.value = false
    }
  }

  // load resolves only once the flag store is actually populated (or the
  // fetch has definitively given up). Still one network call per boot:
  // concurrent callers share the same promise.
  async function load() {
    if (loaded.value) return
    if (!loadPromise) {
      loadPromise = fetchFeatures().finally(() => {
        loadPromise = null
      })
    }
    await loadPromise
  }

  async function refresh() {
    await fetchFeatures()
  }

  function flagRef(flag: FeatureFlag) {
    return computed(() => flags.value[flag] === true)
  }

  // peek returns a raw boolean for non-reactive contexts (route
  // guards, one-shot checks). Don't use inside template/computed —
  // it won't react to refresh().
  function peek(flag: FeatureFlag): boolean {
    return flags.value[flag] === true
  }

  return { flags, loaded, loading, load, refresh, flagRef, peek }
}
